/**
 * usePageTracking.js
 *
 * Custom hook that silently sends a page-view event to the backend on every
 * route change. The beacon is fire-and-forget:
 *   • Uses navigator.sendBeacon when available (survives page unloads).
 *   • Falls back to fetch with keepalive.
 *   • Any network / server error is swallowed — visitor UX is never affected.
 *
 * Usage: call once inside <App /> or any top-level component that is mounted
 * for the lifetime of the application.
 *
 *   import { usePageTracking } from './hooks/usePageTracking';
 *   function App() {
 *     usePageTracking();
 *     return ...;
 *   }
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const TRACK_URL = `${process.env.REACT_APP_API_URL || 'https://quantum-stack.com'}/api/track/pageview`;

/**
 * Returns (or creates) a stable session ID stored in sessionStorage.
 * Each browser tab gets its own ID, which groups visits within a session
 * even when multiple users share the same IP (e.g. corporate NAT).
 */
function getSessionId() {
  const KEY = 'qs_session_id';
  let sid = sessionStorage.getItem(KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(KEY, sid);
  }
  return sid;
}

/**
 * Sends the beacon.  Returns immediately — the network call is fully async.
 */
function sendBeacon(path) {
  try {
    const payload = JSON.stringify({
      path,
      referrer:  document.referrer || null,
      sessionId: getSessionId(),
    });

    // Prefer sendBeacon (non-blocking, survives navigation)
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(TRACK_URL, blob);
      return;
    }

    // Fallback: fetch with keepalive
    fetch(TRACK_URL, {
      method:    'POST',
      headers:   { 'Content-Type': 'application/json' },
      body:      payload,
      keepalive: true,
    }).catch(() => {
      // Silently ignore any error
    });
  } catch {
    // Never bubble up to the user
  }
}

/**
 * usePageTracking
 *
 * Fires a page-view beacon whenever the URL path changes.
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    sendBeacon(location.pathname);
  }, [location.pathname]);
}
